/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable no-param-reassign */
import React, { useState, useCallback, createContext, useEffect, forwardRef, useImperativeHandle } from 'react'
import { Card, ConfigProvider, theme } from 'antd'
import { useSelector } from 'react-redux'
import { State, VisitorContext, NodesById, NodeError, ErrorMap } from './types'
import { customReportGrammarSelector } from '../../../../../../redux/config/customReport/selectors'
import {
  CustomReportCategory,
  CustomReportCategoryType
} from '../../../../../../redux/context/customReports/typesCategory'
import { createBuilderNode, generateRandomId } from './utils'
import TreeNode from './components/TreeNode'

// Tuo funktiot treeOperations.ts tiedostosta
import {
  addChild as addChildOperation,
  removeNode as removeNodeOperation,
  toggleExpand as toggleExpandOperation,
  updateNodeValues as updateNodeValuesOperation,
  setNodeDragging as setNodeDraggingOperation,
  moveNode as moveNodeOperation
} from './components/treeOperations'
import { NormalizedExportVisitor } from './normalizedVisitors/NormalizedExportVisitor'
import { NormalizedValidationVisitor } from './normalizedVisitors/NormalizedValidationVisitor'
import { populateTreeFromData } from './treeImporter'
import { CustomReportSectionsType } from '../../../../../../redux/context/customReports/typesSection'

// Context to provide visitor context throughout the tree
export const TreeContext = createContext<
  VisitorContext & {
    isFunctionDecendant?: boolean
    draggedNodeLevel?: number
    draggedNodeId?: string | null
  }
>({ state: State.Root })

// Komponentin prop-tyypit
interface CategoryBuilderProps {
  initialData?: CustomReportCategory[] // Valinnainen alustusdata
  sectionType: CustomReportSectionsType['type']
  switchRowsAndColumns: boolean
}

// Julkinen rajapinta
export interface CategoryBuilderHandle {
  validate: () => { valid: boolean; errors: NodeError[] }
  getData: () => CustomReportCategory[]
}

// Main component for the financial analysis tree form
const CategoryBuilder = forwardRef<CategoryBuilderHandle, CategoryBuilderProps>(
  ({ initialData, sectionType, switchRowsAndColumns }, ref) => {
    const rules = useSelector(customReportGrammarSelector)
    const [draggedNodeId, setDraggedNodeId] = useState<string | null>(null)
    const [dropIndicator, setDropIndicator] = useState<{
      nodeId: string | null
      position: 'before' | 'after' | 'inside' | null
    }>({ nodeId: null, position: null })

    const {
      token: { colorPrimary }
    } = theme.useToken()

    // Normalisoitu tietorakenne
    const [nodesById, setNodesById] = useState<NodesById>(() => {
      // Jos initialData on annettu, käytä sitä puun alustamiseen
      if (initialData && initialData.length > 0) {
        const { nodesById: initialNodesById } = populateTreeFromData(initialData)
        return initialNodesById
      }

      // Muuten luo tyhjä puu
      const rootId = generateRandomId()
      const rootNode = createBuilderNode(rootId, 'root', { state: State.Root })
      return {
        [rootId]: {
          ...rootNode,
          parentId: null,
          childIds: []
        }
      }
    })

    // Juurisolmujen ID:t
    const [rootIds, setRootIds] = useState<string[]>(() => {
      // Jos initialData on annettu, käytä populateTreeFromData-funktion palauttamaa rootId:tä
      if (initialData && initialData.length > 0) {
        const { rootId } = populateTreeFromData(initialData)
        return [rootId]
      }

      // Muuten käytä ensimmäistä luotua solmua
      return [Object.keys(nodesById)[0]]
    })

    // Add state for validation errors
    const [validationErrors, setValidationErrors] = useState<ErrorMap>({})

    // Update useImperativeHandle
    useImperativeHandle(ref, () => ({
      validate: () => {
        try {
          const validationVisitor = new NormalizedValidationVisitor(rules, nodesById)
          const errors = validationVisitor.validate(rootIds[0])

          // Group errors by nodeId
          const errorMap: ErrorMap = {}
          errors.forEach(error => {
            if (!errorMap[error.nodeId]) errorMap[error.nodeId] = []
            errorMap[error.nodeId].push(error)
          })

          setValidationErrors(errorMap)
          return { valid: errors.length === 0, errors }
        } catch (e) {
          console.error('Validation error:', e)
          return { valid: false, errors: [] }
        }
      },
      getData: () => {
        const exportVisitor = new NormalizedExportVisitor(rules, nodesById)
        return exportVisitor.exportChildren(rootIds[0])
      }
    }))

    // Päivitä puu, jos initialData muuttuu
    useEffect(() => {
      if (initialData && initialData.length > 0) {
        const { nodesById: newNodesById, rootId: newRootId } = populateTreeFromData(initialData)
        setNodesById(newNodesById)
        setRootIds([newRootId])
      }
    }, [initialData])

    // Optimized function to add a child node
    const addChild = useCallback(
      (parentId: string, nodeType: CustomReportCategoryType, context: VisitorContext): void => {
        // Käytä treeOperations.ts tiedoston funktiota
        setNodesById(prev => addChildOperation(prev, parentId, nodeType, context))

        // Varmistustoimenpide uudelleenrenderöintiä varten
        setTimeout(() => {
          setNodesById(prev => ({ ...prev }))
        }, 0)
      },
      []
    )

    // Optimized function to remove a node
    const removeNode = useCallback((nodeId: string): void => {
      // Käytä treeOperations.ts tiedoston funktiota
      setNodesById(prev => removeNodeOperation(prev, nodeId))
    }, [])

    // Optimized function to toggle node expansion
    const toggleExpand = useCallback((nodeId: string): void => {
      // Käytä treeOperations.ts tiedoston funktiota
      setNodesById(prev => toggleExpandOperation(prev, nodeId))

      // Varmistustoimenpide uudelleenrenderöintiä varten
      setTimeout(() => {
        setNodesById(prev => ({ ...prev }))
      }, 0)
    }, [])

    // Optimized function to update node values
    const updateNodeValues = useCallback((nodeId: string, values: Partial<CustomReportCategory>): void => {
      // Käytä treeOperations.ts tiedoston funktiota
      setNodesById(prev => updateNodeValuesOperation(prev, nodeId, values))
    }, [])

    // Function to handle drag start
    const handleDragStart = useCallback((nodeId: string): void => {
      // Aseta draggedNodeId tila
      setDraggedNodeId(nodeId)

      // Käytä treeOperations.ts tiedoston funktiota
      setNodesById(prev => setNodeDraggingOperation(prev, nodeId, true))
    }, [])

    // Function to handle drag end
    const handleDragEnd = useCallback((): void => {
      // Puhdista draggaus, jos draggattava solmu on asetettu
      if (draggedNodeId) {
        // Käytä treeOperations.ts tiedoston funktiota
        setNodesById(prev => setNodeDraggingOperation(prev, draggedNodeId, false))
      }

      // Nollaa draggedNodeId
      setDraggedNodeId(null)
    }, [draggedNodeId])

    // Optimized function to handle drop
    const handleDrop = useCallback(
      (targetNodeId: string, position: 'before' | 'after' | 'inside'): void => {
        if (!draggedNodeId || draggedNodeId === targetNodeId) return

        // Käytä treeOperations.ts tiedoston funktiota
        setNodesById(prev => moveNodeOperation(prev, draggedNodeId, targetNodeId, position))

        // Nollaa draggedNodeId
        setDraggedNodeId(null)
      },
      [draggedNodeId]
    )

    // Function to handle drop indicator
    const handleDropIndicator = useCallback((nodeId: string | null, position: 'before' | 'after' | 'inside' | null) => {
      setDropIndicator({ nodeId, position })
    }, [])

    return (
      <Card styles={{ body: { backgroundColor: '#ebebeb' } }}>
        <ConfigProvider componentSize="small">
          <TreeContext.Provider value={{ state: State.Root, draggedNodeId }}>
            {rootIds.map(rootId => (
              <TreeNode
                key={rootId}
                nodeId={rootId}
                nodes={nodesById}
                rules={rules}
                dropIndicator={dropIndicator}
                colorPrimary={colorPrimary}
                rootId={rootIds[0]} // Välitetään juurisolmun ID
                errors={validationErrors}
                sectionType={sectionType}
                switchRowsAndColumns={switchRowsAndColumns}
                onToggle={toggleExpand}
                onRemove={removeNode}
                onAddChild={addChild}
                onUpdateValues={updateNodeValues}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                onDrop={handleDrop}
                onDropIndicator={handleDropIndicator}
              />
            ))}
          </TreeContext.Provider>
        </ConfigProvider>
      </Card>
    )
  }
)

export default CategoryBuilder
